// src/components/Work.tsx
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink, X } from "lucide-react";
import AnimatedSection from "./AnimatedSection";

interface Project {
  id: number;
  title: string;
  category: string;
  image: string;
  description: string;
  link?: string;
}

const Work = () => {
  const [filter, setFilter] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const projects: Project[] = [
    {
      id: 1,
      title: "Abstract Harmony",
      category: "Painting",
      image: "https://images.unsplash.com/photo-1547891654-e66ed7ebb968",
      description:
        "An exploration of color and form through abstract expressionism, creating a visual representation of musical harmony.",
    },
    {
      id: 2,
      title: "Digital Landscapes",
      category: "Digital Art",
      image: "https://images.unsplash.com/photo-1493246507139-91e8fad9978e",
      description:
        "A series of digital landscapes that blend natural elements with abstract concepts, exploring the intersection of technology and nature.",
    },
    {
      id: 3,
      title: "Urban Reflections",
      category: "Photography",
      image: "https://images.unsplash.com/photo-1496449903678-68ddcb189a24",
      description:
        "A photographic exploration of urban environments, focusing on reflective surfaces and the distortion of familiar cityscapes.",
    },
    {
      id: 4,
      title: "Organic Structures",
      category: "Sculpture",
      image: "https://images.unsplash.com/photo-1554907984-15263bfd63bd",
      description:
        "Three-dimensional works inspired by natural forms and growth patterns, created using a combination of traditional and modern techniques.",
    },
    {
      id: 5,
      title: "Color Study",
      category: "Painting",
      image: "https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b",
      description:
        "A series exploring the emotional and psychological effects of color combinations through abstract compositions.",
    },
    {
      id: 6,
      title: "Conceptual Illustrations",
      category: "Illustration",
      image: "https://images.unsplash.com/photo-1618005198919-d3d4b5a92ead",
      description:
        "Editorial and conceptual illustrations that communicate complex ideas through figurative and symbolic imagery.",
    },
  ];

  const categories = ["All", ...new Set(projects.map((project) => project.category))];

  const filteredProjects =
    filter && filter !== "All" ? projects.filter((project) => project.category === filter) : projects;

  return (
    <section id="work" className="py-24 px-4 bg-secondary/50">
      <div className="container mx-auto max-w-5xl">
        <AnimatedSection className="text-center mb-16" variant="fade-up">
          <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-2">Portfolio</h2>
          <h3 className="text-3xl md:text-4xl font-bold mb-6">Featured Work</h3>
          <div className="h-1 w-20 bg-primary mx-auto"></div>
        </AnimatedSection>

        <AnimatedSection className="flex flex-wrap justify-center gap-2 mb-12" variant="fade-up" delay={200}>
          {categories.map((category, index) => (
            <Button
              key={index}
              variant={filter === category || (category === "All" && !filter) ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(category === "All" ? null : category)}
              className="min-w-24 transition-all duration-300"
            >
              {category}
            </Button>
          ))}
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project, index) => (
            <AnimatedSection key={project.id} variant="fade-up" delay={index * 100}>
              <Card
                className="glass-card overflow-hidden cursor-pointer group transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                onClick={() => setSelectedProject(project)}
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <Button variant="ghost" className="text-white border border-white/30 backdrop-blur-sm">
                      View Project
                    </Button>
                  </div>
                </div>
                <CardContent className="p-4">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                    {project.category}
                  </div>
                  <h4 className="text-lg font-medium">{project.title}</h4>
                </CardContent>
              </Card>
            </AnimatedSection>
          ))}
        </div>
      </div>

      {/* Project Details Modal */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <Card className="glass-card max-w-3xl w-full max-h-[90vh] overflow-y-auto relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 z-10 bg-white/80 backdrop-blur-md border border-border text-foreground rounded-md drop-shadow-sm hover:bg-white/90 hover:shadow-md transition-all duration-200"
              onClick={() => setSelectedProject(null)}
              aria-label="Close project modal"
            >
              <X className="h-5 w-5" />
            </Button>
            <div className="aspect-[16/9] overflow-hidden">
              <img
                src={selectedProject.image}
                alt={selectedProject.title}
                className="w-full h-full object-cover"
              />
            </div>
            <CardContent className="p-6">
              <div className="text-sm uppercase tracking-wider text-muted-foreground mb-1">
                {selectedProject.category}
              </div>
              <h3 className="text-2xl font-display font-bold mb-4">{selectedProject.title}</h3>
              <p className="mb-6 leading-relaxed">{selectedProject.description}</p>
              {selectedProject.link && (
                <Button asChild>
                  <a
                    href={selectedProject.link}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2"
                  >
                    View Project <ExternalLink size={16} />
                  </a>
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </section>
  );
};

export default Work;